import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { MasterVehicle } from '../entities/master-vehicle';
import { MasterVehicleService } from '../services/master-vehicle.service';

/**
 * Master Vehicle data source
 */
export class MasterVehicleDataSource implements DataSource<MasterVehicle> {
  /**
   * Master Vehicle subject
   *
   * @private
   */
  private masterVehiclesSubject = new BehaviorSubject<MasterVehicle[]>([]);

  /**
   * Loading subject
   *
   * @private
   */
  private loadingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Loading observable
   */
  public loading$ = this.loadingSubject.asObservable();

  /**
   * Constructor
   *
   * @param masterVehicleService  service
   */
  constructor(private masterVehicleService: MasterVehicleService) {}

  /**
   * Connect to collection
   *
   * @param collectionViewer CollectionViewer
   *
   * @returns Observable
   */
  connect(collectionViewer: CollectionViewer): Observable<MasterVehicle[]> {
    return this.masterVehiclesSubject.asObservable();
  }

  /**
   * Disconnect from collection
   *
   * @param collectionViewer CollectionViewer
   */
  disconnect(collectionViewer: CollectionViewer): void {
    this.masterVehiclesSubject.complete();
    this.loadingSubject.complete();
  }

  /**
   * Load data from collection
   *
   * @param order Order
   * @param sort Sort
   * @param limit Limit
   * @param page Page
   * @param filter Object with filtered attributes
   */
  load(order: string = 'plate', sort: string = 'asc', limit: number = 10, page: number = 0, filter: object = {}): void {
    this.loadingSubject.next(true);

    const options = { order, sort, limit, page };

    // Column order mapper
    if (options.order === 'barrierProfileName') {
      options.order = 'barrierProfile';
    }

    for (const i in filter) {
      if (filter.hasOwnProperty(i) && filter[i] === '') {
        delete filter[i];
      }
    }

    this.masterVehicleService
      .findFiltered({ ...options, ...filter })
      .pipe(
        catchError(() => of([])),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe((masterVehicles) => this.masterVehiclesSubject.next(masterVehicles));
  }
}
